import { useState, useEffect, useRef } from 'react';

const RETRY_INTERVAL_MS = 100;
const MAX_RETRY_MS = 3000;
const MIN_SYNC_SCROLLERS = 2;

/**
 * Trouve tous les éléments .fc-scroller avec scroll horizontal dans les conteneurs fournis
 * @param {string} selector - Sélecteur CSS (par défaut: '.fc-scroller')
 * @param {any[]} dependencies - Dépendances pour forcer la réinitialisation (ex: [orientation, dateRangeType])
 * @returns {HTMLElement[]} Tableau des éléments trouvés
 */
export function useScrollerElements(selector, dependencies = []) {
  const [elements, setElements] = useState([]);
  const previousElementsRef = useRef([]);

  useEffect(() => {
    setElements([]);
    previousElementsRef.current = [];

    let retryIntervalId = null;
    /** @type {ResizeObserver | null} */
    let resizeObserver = null;
    let resizeRafId = null;
    let observedCandidateCount = 0;
    const retryStartedAt = Date.now();

    const isHorizontallyScrollable = (/** @type {HTMLElement} */ el) => (
      el.scrollWidth > el.clientWidth
    );

    const findScrollable = () => {
      const allScrollers = /** @type {HTMLElement[]} */ (
        Array.from(document.querySelectorAll(selector)).filter(
          (el) => el instanceof HTMLElement,
        )
      );
      const previousSet = new Set(previousElementsRef.current);

      const retained = previousElementsRef.current.filter(
        (el) => el.isConnected,
      );

      const newlyDiscovered = allScrollers.filter(
        (el) => !previousSet.has(el) && isHorizontallyScrollable(el),
      );

      return [...retained, ...newlyDiscovered];
    };

    const updateIfChanged = (scrollableElements) => {
      if (scrollableElements.length >= MIN_SYNC_SCROLLERS) {
        const hasChanged = scrollableElements.length !== previousElementsRef.current.length
          || scrollableElements.some((el, index) => el !== previousElementsRef.current[index]);

        if (hasChanged) {
          previousElementsRef.current = scrollableElements;
          setElements(scrollableElements);
        }

        return true;
      }

      if (previousElementsRef.current.length > 0) {
        previousElementsRef.current = [];
        setElements([]);
      }

      return false;
    };

    const stopRetryLoop = () => {
      if (retryIntervalId !== null) {
        clearInterval(retryIntervalId);
        retryIntervalId = null;
      }
    };

    const scheduleResizeDetection = () => {
      if (resizeRafId !== null) {
        cancelAnimationFrame(resizeRafId);
      }

      resizeRafId = requestAnimationFrame(() => {
        resizeRafId = null;
        const scrollableElements = findScrollable();
        const hasEnough = updateIfChanged(scrollableElements);

        if (hasEnough) {
          stopRetryLoop();
        }
      });
    };

    const observeMatchingElements = () => {
      resizeObserver?.disconnect();
      resizeObserver = new ResizeObserver(() => {
        scheduleResizeDetection();
      });

      const candidates = document.querySelectorAll(selector);
      observedCandidateCount = candidates.length;
      candidates.forEach((el) => {
        resizeObserver?.observe(el);
      });
    };

    const ensureObservingAllCandidates = () => {
      const candidateCount = document.querySelectorAll(selector).length;

      if (candidateCount !== observedCandidateCount) {
        observeMatchingElements();
      }
    };

    const runDetection = () => {
      ensureObservingAllCandidates();
      const scrollableElements = findScrollable();
      const hasEnough = updateIfChanged(scrollableElements);

      if (hasEnough) {
        stopRetryLoop();
      } else if (Date.now() - retryStartedAt >= MAX_RETRY_MS) {
        stopRetryLoop();
      }
    };

    observeMatchingElements();
    runDetection();
    retryIntervalId = setInterval(runDetection, RETRY_INTERVAL_MS);

    return () => {
      stopRetryLoop();
      if (resizeRafId !== null) {
        cancelAnimationFrame(resizeRafId);
      }
      resizeObserver?.disconnect();
    };
  }, dependencies);

  return elements;
}

export default useScrollerElements;
